import { useState } from "react";
import { Input } from "../../components/ui/Input";
import { useToast } from "../../components/ui/Toast";
import { Hint } from "../../components/ui/Tooltip";
import { useSetOptionCategory } from "./useGiftOptions";

const CATEGORY_HELP =
	"Options sharing a category are grouped: the shopper picks the category first, then the item. It only appears once there are two or more groups. Options left blank are shown as “More options”.";

type OptionCategoryFieldProps = {
	campaignId: string;
	giftId: string;
	optionId: string;
	category: string | null;
	/** One datalist per gift, rendered by the editor, so its suggestions are shared. */
	listId: string;
};

/**
 * A draft only while typing: the parent remounts this on a saved change, so a
 * refetch never leaves it showing a stale value.
 */
export const OptionCategoryField = ({
	campaignId,
	giftId,
	optionId,
	category,
	listId,
}: OptionCategoryFieldProps) => {
	const setCategory = useSetOptionCategory(campaignId, giftId);
	const toast = useToast();
	const [value, setValue] = useState(category ?? "");

	const commit = () => {
		const next = value.trim() || null;
		if (next === category) return;
		setCategory.mutate(
			{ optionId, category: next },
			{ onError: (error) => toast.push(error.message, "error") },
		);
	};

	return (
		<span className="flex items-center gap-1.5 text-xs text-ink-muted">
			Category
			<Input
				value={value}
				onChange={(event) => setValue(event.target.value)}
				onBlur={commit}
				onKeyDown={(event) => {
					if (event.key === "Enter") event.currentTarget.blur();
				}}
				list={listId}
				maxLength={40}
				placeholder="e.g. Hats"
				aria-label="Category"
				className="h-7 w-32 text-xs"
			/>
			<Hint label="About categories">{CATEGORY_HELP}</Hint>
		</span>
	);
};
